import { isChecklistItemApplicable } from './checklist-visibility';
import {
  BRIDGE_DATE_OF_BIRTH_KEY,
  BRIDGE_INITIAL_BMI_EVIDENCE_KEY,
  BRIDGE_INITIAL_BMI_KEY,
  BRIDGE_THERAPY_COURSE_KEY,
  BRIDGE_THERAPY_START_KEY,
  localToday,
  showTherapyStartInput,
} from './clinical-prefill';
import {
  MedicareBridgeCaseView,
  MedicareBridgeChecklistItem,
  MedicareBridgeResponseValue,
} from './medicare-bridge.types';

const LEGACY_CONDITION_KEY = 'qualifying_conditions';
const BMI_30_KEY = 'qualifying_conditions_bmi_30';
const BMI_27_KEY = 'qualifying_conditions_bmi_27';

export function initialReviewResponses(
  bridgeCase: MedicareBridgeCaseView,
): Record<string, MedicareBridgeResponseValue> {
  const items = new Set(bridgeCase.checklist.items.map((item) => item.key));
  const saved = bridgeCase.latestReview?.responses ?? {};
  const responses: Record<string, MedicareBridgeResponseValue> = {};
  for (const [key, value] of Object.entries(saved)) {
    if (!items.has(key)) continue;
    responses[key] = Array.isArray(value) ? [...value] : value;
  }

  if (
    items.has('treatment_purpose') &&
    saved['prescription_intent'] === 'CARDIOVASCULAR_RISK_REDUCTION'
  ) {
    responses['treatment_purpose'] = 'CARDIOVASCULAR_RISK_REDUCTION';
  }

  const legacyConditions = saved[LEGACY_CONDITION_KEY];
  const bmi = saved['initial_bmi'];
  if (Array.isArray(legacyConditions) && typeof bmi === 'number') {
    const key =
      bmi >= 30 && bmi < 35 ? BMI_30_KEY : bmi >= 27 && bmi < 30 ? BMI_27_KEY : null;
    const item = key
      ? bridgeCase.checklist.items.find((entry) => entry.key === key)
      : undefined;
    const offered = new Set(item?.options?.map((option) => option.value) ?? []);
    const kept = legacyConditions.filter(
      (value): value is string => typeof value === 'string' && offered.has(value),
    );
    if (key && items.has(key) && responses[key] == null && kept.length > 0) {
      responses[key] = kept;
    }
  }

  return responses;
}

export function documentedTherapyStart(
  bridgeCase: MedicareBridgeCaseView,
): string | null {
  const value = bridgeCase.latestReview?.responses?.[BRIDGE_THERAPY_START_KEY];
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : null;
}

/** The new-start therapy date: today, or the corrected review's date. */
export function newStartDate(
  bridgeCase: MedicareBridgeCaseView,
  correcting: boolean,
  today: string,
): string {
  const review = bridgeCase.latestReview;
  if (!correcting || !review) return today;
  const documented = documentedTherapyStart(bridgeCase);
  if (review.responses?.[BRIDGE_THERAPY_COURSE_KEY] === 'NEW_START' && documented) {
    return documented;
  }
  const createdAt = new Date(review.createdAt);
  return Number.isNaN(createdAt.getTime()) ? today : localToday(createdAt);
}

export function reviewPromptItems(
  items: MedicareBridgeChecklistItem[],
  responses: Record<string, MedicareBridgeResponseValue>,
  documentedDate: string | null,
  correcting: boolean,
): MedicareBridgeChecklistItem[] {
  return items.filter((item) => {
    if (item.key === BRIDGE_INITIAL_BMI_EVIDENCE_KEY) return false;
    if (!isChecklistItemApplicable(item, responses, items)) return false;
    if (
      item.key === BRIDGE_THERAPY_START_KEY &&
      !showTherapyStartInput(
        responses[BRIDGE_THERAPY_COURSE_KEY],
        documentedDate,
        correcting,
      )
    ) {
      return false;
    }
    return true;
  });
}

export function derivedReviewResponses(input: {
  items: MedicareBridgeChecklistItem[];
  responses: Record<string, MedicareBridgeResponseValue>;
  documentedDate: string | null;
  correcting: boolean;
  dateOfBirth: string | null | undefined;
  bmiEvidence: string | null;
  newStartDate?: string;
  /** The provider opened the documented continuing-therapy date for correction. */
  editingTherapyStart?: boolean;
}): Record<string, MedicareBridgeResponseValue> {
  const startDate = input.newStartDate ?? localToday();
  const next: Record<string, MedicareBridgeResponseValue> = { ...input.responses };
  const keys = new Set(input.items.map((item) => item.key));

  if (keys.has(BRIDGE_DATE_OF_BIRTH_KEY) && input.dateOfBirth !== undefined) {
    if (input.dateOfBirth) next[BRIDGE_DATE_OF_BIRTH_KEY] = input.dateOfBirth;
    else delete next[BRIDGE_DATE_OF_BIRTH_KEY];
  }

  if (keys.has(BRIDGE_INITIAL_BMI_EVIDENCE_KEY)) {
    const bmi = next[BRIDGE_INITIAL_BMI_KEY];
    if (bmi != null && bmi !== '' && input.bmiEvidence) {
      next[BRIDGE_INITIAL_BMI_EVIDENCE_KEY] = input.bmiEvidence;
    } else {
      delete next[BRIDGE_INITIAL_BMI_EVIDENCE_KEY];
    }
  }

  const course = next[BRIDGE_THERAPY_COURSE_KEY];
  if (keys.has(BRIDGE_THERAPY_START_KEY)) {
    if (course === 'NEW_START') next[BRIDGE_THERAPY_START_KEY] = startDate;
    else if (
      course === 'CONTINUING' &&
      input.documentedDate &&
      !input.correcting &&
      !input.editingTherapyStart
    ) {
      next[BRIDGE_THERAPY_START_KEY] = input.documentedDate;
    }
  }

  const applicable = new Set(
    input.items
      .filter((item) => isChecklistItemApplicable(item, next, input.items))
      .map((item) => item.key),
  );
  if (!applicable.has(BRIDGE_INITIAL_BMI_KEY)) {
    applicable.delete(BRIDGE_INITIAL_BMI_EVIDENCE_KEY);
  }
  return Object.fromEntries(
    Object.entries(next).filter(([key]) => applicable.has(key)),
  );
}
